// STAGING-LAUNCH-READINESS-002-R2 — customer cabinet API client ("Мої замовлення").
// Ownership resolved server-side from session_key only — never sends/trusts a customer id.
//
// STAGING-LAUNCH-READINESS-002-R2-R2 — stale-while-revalidate in-memory cache +
// bounded fetch. Root cause of the reported "Мої замовлення" spinner/hang: the
// bare fetch() here had no timeout, so a slow Worker left MyOrdersV2/OrderDetailV2
// stuck on "Loading…" forever, and every re-visit re-fetched from empty state
// (no cache), so "back" always re-showed a blank spinner instead of the list the
// customer had already seen this session.
import { waitForTelegramInitData } from "../lib/telegram.js";
import { fetchWithTimeout, isFetchTimeout } from "./fetch-timeout-v2.js";
import { emitRoutePerf } from "./perf-events-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";

export type CustomerReceiptState =
  | "not_needed"
  | "unpaid"
  | "awaiting_payment"
  | "paid"
  | "queued"
  | "creating"
  | "awaiting_fiscalization"
  | "forming"
  | "ready"
  | "fiscalized"
  | "delay"
  | "error"
  | "identity_missing"
  | "refund_pending"
  | "refund_done"
  | "unavailable";

export interface CustomerReceiptSection {
  state: CustomerReceiptState;
  fiscal_number: string | null;
  receipt_url: string | null;
  issued_at: string | null;
  amount_uah: number | null;
  payment_method?: string | null;
  payment_method_label_uk?: string | null;
  test_badge?: boolean;
}

export interface CustomerOrderSummary {
  order_id: string;
  display_order_number: string;
  status: string;
  order_top_status: string | null;
  payment_status: string;
  payment_method: string | null;
  /** HR01 — when set, display paid even if payment_status is stale review/pending. */
  bank_confirmed_at?: string | null;
  total_uah: number | null;
  created_at: string;
  receipt: CustomerReceiptSection;
  /** STAGING-LAUNCH-READINESS-002-R2-R2 — up to 3 snapshot thumbnails (null = no image on that line). */
  thumbnails: (string | null)[];
  items_count: number;
  other_items_count: number;
  /** COMMERCE-LAUNCH-167A-R6 — first line preview (name/qty/image) from list DTO. */
  preview_item?: {
    name: string;
    image_url: string | null;
    qty: number;
  } | null;
  status_rail?: string;
  display_state?: string | null;
  payment_timing?: string | null;
  ttn_status?: string | null;
  canonical_projection?: { primary_order_state?: string | null } | null;
  /** COMMERCE-CORE-R1 — optional documents summary for Cabinet Documents home. */
  documents_count?: number;
  documents_status_uk?: string | null;
}

export interface CustomerOrderItem {
  product_id: string;
  /** Customer-facing store SKU only when snapshotted; never product_id. */
  sku: string | null;
  original_sku?: string | null;
  barcode?: string | null;
  title: string;
  qty: number;
  unit_price_uah: number | null;
  line_total_uah: number | null;
  /** Snapshot image from the order line if one was captured at checkout time; null → client placeholder. */
  image_url: string | null;
}

export interface CustomerOrderDetail {
  order_id: string;
  display_order_number: string;
  status: string;
  order_top_status: string | null;
  payment_status: string;
  payment_method: string | null;
  payment_timing?: string | null;
  delivery_method: string | null;
  np_status: string | null;
  ttn_status: string;
  delivery_city: string | null;
  delivery_warehouse: string | null;
  total_uah: number | null;
  subtotal_uah: number | null;
  discount_uah: number | null;
  created_at: string;
  /** K21 — only when the bank actually confirmed; never invented. */
  bank_confirmed_at?: string | null;
}

/** STAGING-LAUNCH-READINESS-002-R2-R2 — «🚚 Доставка і ТТН» cabinet block. Read-only. */
export type CustomerDeliveryState = "not_set" | "pending_ttn" | "ttn_created";

export interface CustomerShipmentCabinetView {
  shipment_id: string;
  slot_index: number;
  shipment_number: number;
  ttn_number: string | null;
  customer_visible_comment: string | null;
  status: string | null;
  updated_at: string | null;
  track_url: string | null;
  timeline: Array<{ event_type: string; created_at: string }>;
  /** F6ZC — items allocated to this slot only. */
  items?: Array<{
    product_id: string;
    title: string;
    qty: number;
    image_url: string | null;
  }>;
}

export interface CustomerDeliverySection {
  state: CustomerDeliveryState;
  method: string | null;
  city: string | null;
  warehouse: string | null;
  ttn_number: string | null;
  tracking_status: string | null;
  /** MANAGER-ORDERS-133 — up to 3 active shipments; never includes internal notes. */
  shipments?: CustomerShipmentCabinetView[];
}

export interface CustomerOrderDetailResponse {
  order: CustomerOrderDetail;
  items: CustomerOrderItem[];
  timeline: Array<{
    status: string;
    note: string | null;
    created_at: string;
    title_uk?: string;
    explanation_uk?: string;
    shipment_number?: number | null;
    receipt_link?: string | null;
    active?: boolean;
    event_type?: string;
  }>;
  receipt: CustomerReceiptSection;
  delivery: CustomerDeliverySection;
  timeline_history?: Array<{
    status: string;
    note: string | null;
    created_at: string;
    active?: boolean;
  }>;
  canonical_projection?: {
    primary_order_state?: string;
    payment_state?: string;
    fiscal_state?: string;
    status_rail?: string;
    awaiting_payment?: boolean;
    forming_receipt?: boolean;
    display_state?: string;
  };
  display_state?: string | null;
  cancel_customer_reason?: string | null;
  cancelled_at?: string | null;
  payment_evidence?: Array<{ receipt_id: string; created_at: string; status: "ACCEPTED" }>;
  documents?: Array<{
    type: "INVOICE" | "PAYMENT_CONFIRMATION" | "SALES_DOCUMENT" | "FISCAL_RECEIPT" | "REFUND_DOCUMENT";
    title_uk: string;
    status: "ready" | "pending" | "unavailable";
    status_chip?: "created" | "available" | "awaiting_signature" | "signed" | "rejected";
    status_uk?: string;
    url?: string;
    created_at: string;
    document_id?: string;
    document_number?: string;
    public_order_number?: string;
  }>;
}

export type CabinetFetchOutcome<T> =
  | { ok: true; data: T; fromCache: false }
  // `notFound`: a definitive server response says this order/session pairing
  // does not resolve — retrying will not help, unlike a timeout/network error.
  | { ok: false; timeout: boolean; notFound: boolean };

const ORDERS_CACHE_TTL_MS = 8_000;
const DETAIL_CACHE_TTL_MS = 8_000;

type CacheEntry<T> = { data: T; at: number };

const ordersCache = new Map<string, CacheEntry<CustomerOrderSummary[]>>();
const detailCache = new Map<string, CacheEntry<CustomerOrderDetailResponse>>();

function isFresh(at: number, ttlMs: number): boolean {
  return Date.now() - at < ttlMs;
}

/** Synchronous read for the "show cached list instantly, refresh in background" pattern. */
export function getCachedOrdersList(sessionKey: string): CustomerOrderSummary[] | null {
  return ordersCache.get(sessionKey)?.data ?? null;
}

export function isCachedOrdersListFresh(sessionKey: string): boolean {
  const entry = ordersCache.get(sessionKey);
  return Boolean(entry && isFresh(entry.at, ORDERS_CACHE_TTL_MS));
}

export function getCachedOrderDetail(sessionKey: string, orderId: string): CustomerOrderDetailResponse | null {
  return detailCache.get(`${sessionKey}:${orderId}`)?.data ?? null;
}

export function invalidateOrderDetailCache(sessionKey: string, orderId: string): void {
  detailCache.delete(`${sessionKey}:${orderId}`);
  ordersCache.delete(sessionKey);
}

export function isCachedOrderDetailFresh(sessionKey: string, orderId: string): boolean {
  const entry = detailCache.get(`${sessionKey}:${orderId}`);
  return Boolean(entry && isFresh(entry.at, DETAIL_CACHE_TTL_MS));
}

/**
 * Fetch the customer's orders list. Never throws / never hangs past the fetch
 * timeout. On timeout or network error, returns `{ ok: false }` and — unlike the
 * old contract — does NOT silently coerce to an empty list, so the caller can
 * keep showing a previously cached list instead of a misleading "no orders yet".
 */
export async function fetchMyOrdersBounded(sessionKey: string): Promise<CabinetFetchOutcome<CustomerOrderSummary[]>> {
  const start = performance.now();
  emitRoutePerf({ route: "orders", stage: "start", durationMs: 0 });
  try {
    // COMMERCE-LAUNCH-167A-R3 — wait for Telegram initData before first cabinet GET
    // (sync headers alone raced cold WebApp → 401 / empty then retry “works”).
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      `/api/store-v2/orders?session_key=${encodeURIComponent(sessionKey)}`,
      {
        method: "GET",
        headers: storeV2AuthHeaders(),
      },
      10_000,
    );
    const json = (await res.json()) as { ok?: boolean; orders?: CustomerOrderSummary[] };
    if (!res.ok || json.ok === false || !Array.isArray(json.orders)) {
      emitRoutePerf({ route: "orders", stage: "error", durationMs: performance.now() - start });
      return { ok: false, timeout: false, notFound: false };
    }
    ordersCache.set(sessionKey, { data: json.orders, at: Date.now() });
    emitRoutePerf({ route: "orders", stage: "ready", durationMs: performance.now() - start, cache: "miss" });
    return { ok: true, data: json.orders, fromCache: false };
  } catch (err) {
    const timeout = isFetchTimeout(err);
    emitRoutePerf({ route: "orders", stage: timeout ? "timeout" : "error", durationMs: performance.now() - start });
    return { ok: false, timeout, notFound: false };
  }
}

/** Legacy-shaped helper kept for call sites that only want a best-effort list (never null). */
export async function fetchMyOrders(sessionKey: string): Promise<CustomerOrderSummary[]> {
  const cached = getCachedOrdersList(sessionKey);
  const res = await fetchMyOrdersBounded(sessionKey);
  if (res.ok) return res.data;
  return cached ?? [];
}

export async function fetchOrderDetailBounded(
  sessionKey: string,
  orderId: string,
): Promise<CabinetFetchOutcome<CustomerOrderDetailResponse>> {
  const start = performance.now();
  emitRoutePerf({ route: "order-detail", stage: "start", durationMs: 0 });
  try {
    await waitForTelegramInitData(2500);
    const qs = new URLSearchParams({ session_key: sessionKey, order_id: orderId });
    const res = await fetchWithTimeout(
      `/api/store-v2/order-detail?${qs}`,
      {
        method: "GET",
        headers: storeV2AuthHeaders(),
      },
      10_000,
    );
    const json = (await res.json()) as
      | ({ ok: true } & CustomerOrderDetailResponse)
      | { ok: false; error?: string };
    if (!res.ok || json.ok === false) {
      emitRoutePerf({ route: "order-detail", stage: "error", durationMs: performance.now() - start });
      // A definitive 4xx / ok:false response (wrong session, unknown order) is
      // not something a retry can fix — surface it as "not found", not degraded.
      const notFound = !res.ok ? res.status === 404 || res.status === 403 : true;
      return { ok: false, timeout: false, notFound };
    }
    const data: CustomerOrderDetailResponse = {
      order: json.order,
      items: json.items,
      timeline: json.timeline,
      receipt: json.receipt,
      delivery: json.delivery,
      ...(json.timeline_history ? { timeline_history: json.timeline_history } : {}),
      ...(json.canonical_projection ? { canonical_projection: json.canonical_projection } : {}),
      ...(json.payment_evidence ? { payment_evidence: json.payment_evidence } : {}),
      ...(json.display_state ? { display_state: json.display_state } : {}),
      ...((json as CustomerOrderDetailResponse).documents
        ? { documents: (json as CustomerOrderDetailResponse).documents }
        : {}),
    };
    detailCache.set(`${sessionKey}:${orderId}`, { data, at: Date.now() });
    emitRoutePerf({ route: "order-detail", stage: "ready", durationMs: performance.now() - start, cache: "miss" });
    return { ok: true, data, fromCache: false };
  } catch (err) {
    const timeout = isFetchTimeout(err);
    emitRoutePerf({
      route: "order-detail",
      stage: timeout ? "timeout" : "error",
      durationMs: performance.now() - start,
    });
    return { ok: false, timeout, notFound: false };
  }
}

/** Legacy-shaped helper kept for call sites that only want best-effort detail (nullable). */
export async function fetchOrderDetail(
  sessionKey: string,
  orderId: string,
): Promise<CustomerOrderDetailResponse | null> {
  const res = await fetchOrderDetailBounded(sessionKey, orderId);
  if (res.ok) return res.data;
  return getCachedOrderDetail(sessionKey, orderId);
}

/** Optional light prefetch (fire-and-forget) so opening a recent order's detail feels instant. */
export function prefetchOrderDetail(sessionKey: string, orderId: string): void {
  if (getCachedOrderDetail(sessionKey, orderId)) return;
  void fetchOrderDetailBounded(sessionKey, orderId).catch(() => {
    /* best-effort prefetch only */
  });
}

export type CustomerCancelOrderOutcome =
  | { ok: true; already_cancelled: boolean }
  | { ok: false; error: string; message_uk?: string };

/**
 * STAGING-LAUNCH-READINESS-002-R2-R2 — customer self-service cancel of an unpaid order.
 * Invalidates the list/detail caches on success so the next render reflects `canceled`.
 */
export async function cancelCustomerOrder(
  sessionKey: string,
  orderId: string,
  reason?: string,
  opts?: { reasonCode?: string | null; requestOnly?: boolean },
): Promise<CustomerCancelOrderOutcome> {
  try {
    const res = await fetchWithTimeout("/api/store-v2/cancel-order", {
      method: "POST",
      headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        order_id: orderId,
        session_key: sessionKey,
        reason: reason ?? null,
        reason_code: opts?.reasonCode ?? null,
        request_only: opts?.requestOnly === true,
      }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      error?: string;
      message_uk?: string;
      idempotent?: boolean;
    };
    if (!res.ok || json.ok !== true) {
      return {
        ok: false,
        error: json.error ?? "cancel_failed",
        ...(json.message_uk ? { message_uk: json.message_uk } : {}),
      };
    }
    ordersCache.delete(sessionKey);
    detailCache.delete(`${sessionKey}:${orderId}`);
    return { ok: true, already_cancelled: Boolean(json.idempotent) };
  } catch {
    return { ok: false, error: "network_error" };
  }
}
